import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Phone, Pickaxe, Hammer, Truck, LayoutGrid, Trees, Droplets, Route, ChevronDown } from "lucide-react";
import { api, jobPhotoSrc } from "../lib/api";
import { SERVICE_DETAILS } from "../lib/serviceDetails";
import { BookingForm } from "../components/BookingForm";
import { usePageTitle } from "../hooks/usePageTitle";

const ICONS = { Pickaxe, Hammer, Truck, LayoutGrid, Trees, Droplets, Route };

function useFaqJsonLd(svc) {
  useEffect(() => {
    if (!svc?.faqs?.length) return;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: svc.faqs.map((f) => ({
        "@type": "Question", name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [svc]);
}

function FaqItem({ f, idx, slug }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-2 border-[#2C3539]/10 bg-white">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`service-faq-${slug}-${idx}`}
        className="w-full flex items-center justify-between gap-4 text-left px-5 md:px-6 py-4"
      >
        <span className="font-display text-lg md:text-xl uppercase text-[#23282B]">{f.q}</span>
        <ChevronDown size={18} className={`shrink-0 text-[#2F5F80] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <p className="px-5 md:px-6 pb-5 text-sm md:text-base text-[#23282B]/80 leading-relaxed" data-testid={`service-faq-answer-${slug}-${idx}`}>
          {f.a}
        </p>
      )}
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const svc = SERVICE_DETAILS[slug];
  usePageTitle(svc ? svc.title : "Services", svc ? `${svc.tagline} AV Contracting serves Vancouver Island and the Lower Mainland — free written estimates.` : undefined);
  useFaqJsonLd(svc);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    if (!svc) return;
    setPhotos([]);
    api.get("/jobs")
      .then((r) => {
        const rows = (r.data || []).filter((j) => svc.galleryFilter.includes(j.service_type));
        setPhotos(rows.slice(0, 3));
      })
      .catch((e) => console.warn("Service detail: failed to load gallery photos", e));
  }, [slug, svc]);

  if (!svc) return <Navigate to="/services" replace />;
  const Icon = ICONS[svc.icon] || Hammer;

  return (
    <div className="pt-20 md:pt-24" data-testid={`service-detail-${slug}`}>
      {/* Header */}
      <section className="bg-[#2C3539] text-white av-grid">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-24">
          <Link to="/services" data-testid="service-detail-back" className="text-[#9CC4DB] hover:text-white text-xs tracking-[0.25em] uppercase font-bold inline-flex items-center gap-2">
            <ArrowLeft size={14} /> All Services
          </Link>
          <div className="mt-6 flex items-start gap-5">
            <Icon size={48} className="text-[#9CC4DB] shrink-0 mt-2 hidden sm:block" />
            <div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.95]" data-testid="service-detail-title">{svc.title}.</h1>
              {svc.subtitle && <div className="mt-2 text-sm font-bold uppercase tracking-[0.3em] text-[#9CC4DB]" data-testid="service-detail-subtitle">{svc.subtitle}</div>}
              <p className="mt-4 max-w-2xl text-white/75 text-base md:text-lg">{svc.tagline}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7" data-testid="service-detail-content">
            {svc.paragraphs.map((p, i) => (
              <p key={i} className={`text-[#23282B]/85 text-base md:text-lg leading-relaxed ${i > 0 ? "mt-6" : ""}`}>{p}</p>
            ))}
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="tel:+12508861112" data-testid="service-detail-call" className="inline-flex items-center gap-2 border-2 border-[#23282B] hover:bg-[#23282B] hover:text-white text-[#23282B] text-xs font-bold tracking-[0.15em] px-5 py-3 uppercase transition-colors">
                <Phone size={14} /> (250) 886-1112
              </a>
              <Link to="/past-jobs" data-testid="service-detail-pastjobs" className="inline-flex items-center gap-2 text-[#2F5F80] hover:text-[#234B66] text-xs font-bold tracking-[0.15em] px-2 py-3 uppercase">
                See our work <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <aside className="lg:col-span-5">
            {photos.length > 0 ? (
              <div className="grid grid-cols-2 gap-3" data-testid="service-detail-gallery">
                {photos.map((j, i) => (
                  <img
                    key={j.id}
                    src={jobPhotoSrc(j)}
                    alt={`${svc.title} — ${j.job_site_name}`}
                    loading="lazy"
                    className={`w-full object-cover border-2 border-[#2C3539]/10 ${i === 0 ? "col-span-2 h-56 md:h-64" : "h-36 md:h-44"}`}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-[#23282B] text-white p-8 border-l-4 border-[#9CC4DB]">
                <div className="font-display text-2xl uppercase">Free Assessments</div>
                <p className="mt-2 text-sm text-white/75 leading-relaxed">Not sure what your site needs? Send photos with the form below and we'll tell you straight — no obligation.</p>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* FAQs */}
      {svc.faqs?.length > 0 && (
        <section className="bg-[#F1F3F5] border-t border-[#2C3539]/10" data-testid="service-detail-faqs">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-14 md:py-16">
            <div className="max-w-[900px] mx-auto">
              <div className="text-[#2F5F80] text-xs tracking-[0.35em] font-bold uppercase">Common Questions</div>
              <h2 className="mt-2 font-display text-3xl md:text-4xl uppercase text-[#23282B]">{svc.title} FAQs</h2>
              <div className="mt-6 space-y-3">
                {svc.faqs.map((f, i) => (
                  <FaqItem key={f.q} f={f} idx={i} slug={slug} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Quote form underneath */}
      <section className="bg-[#F1F3F5]" data-testid="service-detail-quote">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 py-16 md:py-20">
          <div className="max-w-[900px] mx-auto">
            <BookingForm defaultService={svc.bookingService} heading={`Get a ${svc.title} quote`} />
          </div>
        </div>
      </section>
    </div>
  );
}
